<template>
  <div>
    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>

    <!-- Vendor Management Section -->
    <div class="vendor-iq-page">
      <div class="vendor-iq-header">
        <div>
          <h2 class="vendor-iq-title">VendorIQ</h2>
          <div class="vendor-iq-summary">
            <span>{{ filteredVendors.length }} รายการ</span>
            <span>Draft {{ draftStepCount }}</span>
            <span>Waiting {{ waitingStepCount }}</span>
            <span>Done {{ doneStepCount }}</span>
            <span>Exists {{ alreadyExistedStepCount }}</span>
            <span>Selected {{ selectedVendorIds.length }}</span>
          </div>
        </div>
        <div class="vendor-iq-actions">
          <!-- Bulk Delete Button -->
          <v-btn
            outlined
            color="error"
            :disabled="selectedVendorIds.length === 0 || isInitializing"
            @click="confirmBulkDelete"
          >
            <v-icon left>mdi-delete</v-icon>
            Bulk Delete
          </v-btn>
          <!-- Bulk Step 1: promote selected step-1 drafts to PurchaseInvoiceVendors. -->
          <v-btn
            color="primary"
            :loading="isSubmittingBulk"
            :disabled="selectedStep1Count === 0 || isSubmittingBulk"
            @click="bulkSubmit"
          >
            <v-icon left>mdi-check</v-icon>
            Submit ({{ selectedStep1Count }})
          </v-btn>
          <!-- Bulk Step 2: push selected step-2 drafts to Express. -->
          <v-btn
            color="success"
            :loading="isSyncingExpressBulk"
            :disabled="selectedStep2Count === 0 || isSyncingExpressBulk"
            @click="bulkSaveToExpress"
          >
            <v-icon left>mdi-cloud-upload</v-icon>
            Save to Express ({{ selectedStep2Count }})
          </v-btn>
        </div>
      </div>

      <!-- Vendor Search and Table -->
      <div class="vendor-table-card">
        <div class="vendor-toolbar">
          <v-text-field
            v-model="vendorSearch"
            dense
            outlined
            class="vendor-search"
            placeholder="ค้นหาผู้จำหน่าย"
            prepend-inner-icon="mdi-magnify"
            hide-details
          ></v-text-field>
          <div class="vendor-toolbar__hint">
            เรียงตาม Step และรหัสผู้จำหน่าย
          </div>
        </div>

        <div class="vendor-table-wrap">
          <v-progress-linear
            indeterminate
            color="primary"
            :active="isRefreshing"
            class="vendor-progress"
          ></v-progress-linear>

        <div
          v-if="showInitialSkeleton"
          class="vendor-skeleton"
          aria-busy="true"
          aria-live="polite"
        >
          <div class="vendor-skeleton__inner">
            <div class="vendor-skeleton__header">
              <div class="vendor-skeleton__cell" style="width: 50px"></div>
              <div class="vendor-skeleton__cell" style="width: 60px">ลำดับ</div>
              <div class="vendor-skeleton__cell" style="width: 110px">Step</div>
              <div class="vendor-skeleton__cell" style="width: 200px">vendTaxid</div>
              <div class="vendor-skeleton__cell" style="width: 90px">ละเว้น</div>
              <div class="vendor-skeleton__cell" style="width: 90px">รวมภาษี</div>
              <div class="vendor-skeleton__cell" style="width: 120px">ต่างประเทศ</div>
              <div class="vendor-skeleton__cell" style="width: 130px">รหัสผู้จำหน่าย</div>
              <div class="vendor-skeleton__cell" style="width: 150px">คำนำหน้า</div>
              <div class="vendor-skeleton__cell" style="width: 250px">ชื่อผู้จำหน่าย</div>
              <div class="vendor-skeleton__cell" style="width: 120px">รหัสสาขา</div>
              <div class="vendor-skeleton__cell" style="width: 160px">รหัสค่าใช้จ่าย</div>
              <div class="vendor-skeleton__cell vendor-skeleton__cell--flex">ที่อยู่</div>
              <div class="vendor-skeleton__cell" style="width: 280px">Action</div>
              <div class="vendor-skeleton__cell" style="width: 200px">Remarks</div>
            </div>
            <div
              v-for="n in skeletonRowCount"
              :key="n"
              class="vendor-skeleton__row"
            >
              <div class="vendor-skeleton__cell" style="width: 50px">
                <div class="skel-bar skel-bar--checkbox"></div>
              </div>
              <div class="vendor-skeleton__cell" style="width: 60px">
                <div class="skel-bar" style="width: 24px"></div>
              </div>
              <div class="vendor-skeleton__cell" style="width: 110px">
                <div class="skel-bar" style="width: 60%"></div>
              </div>
              <div class="vendor-skeleton__cell" style="width: 200px">
                <div class="skel-bar" style="width: 70%"></div>
              </div>
              <div class="vendor-skeleton__cell" style="width: 90px">
                <div class="skel-bar" style="width: 50%"></div>
              </div>
              <div class="vendor-skeleton__cell" style="width: 90px">
                <div class="skel-bar" style="width: 50%"></div>
              </div>
              <div class="vendor-skeleton__cell" style="width: 120px">
                <div class="skel-bar" style="width: 60%"></div>
              </div>
              <div class="vendor-skeleton__cell" style="width: 130px">
                <div class="skel-bar" style="width: 70%"></div>
              </div>
              <div class="vendor-skeleton__cell" style="width: 150px">
                <div class="skel-bar" style="width: 60%"></div>
              </div>
              <div class="vendor-skeleton__cell" style="width: 250px">
                <div class="skel-bar" style="width: 85%"></div>
              </div>
              <div class="vendor-skeleton__cell" style="width: 120px">
                <div class="skel-bar" style="width: 60%"></div>
              </div>
              <div class="vendor-skeleton__cell" style="width: 160px">
                <div class="skel-bar" style="width: 80%"></div>
              </div>
              <div class="vendor-skeleton__cell vendor-skeleton__cell--flex">
                <div class="skel-bar" style="width: 90%"></div>
              </div>
              <div class="vendor-skeleton__cell" style="width: 280px">
                <div class="skel-bar skel-bar--circle"></div>
                <div class="skel-bar skel-bar--circle"></div>
                <div class="skel-bar" style="width: 60px; height: 24px"></div>
              </div>
              <div class="vendor-skeleton__cell" style="width: 200px">
                <div class="skel-bar" style="width: 70%"></div>
              </div>
            </div>
          </div>
        </div>

        <el-table
          v-else-if="!isInitialLoading"
          class="vendor-iq-table"
          :data="paginatedVendors"
          style="width: 100%"
          empty-text="ไม่พบผู้จำหน่ายที่สร้างจากระบบทะเบียน"
          @selection-change="handleSelectionChange"
          :default-sort="{prop: 'venCode', order: 'ascending'}"
        >
          <el-table-column type="selection" width="50"></el-table-column>
          <el-table-column prop="index" label="ลำดับ" width="60" align="center">
            <template slot-scope="scope">
              <span class="vendor-index">
                {{ (vendorPage - 1) * vendorPageSize + scope.$index + 1 }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="vendorIqStep" label="Step" width="110" align="center" sortable>
            <template slot-scope="scope">
              <span
                v-if="scope.row.vendorIqStep === STEP.DRAFT"
                class="vendor-step vendor-step--draft"
              >
                <span class="vendor-step__dot"></span>
                Draft
              </span>
              <span
                v-else-if="scope.row.vendorIqStep === STEP.WAITING"
                class="vendor-step vendor-step--waiting"
              >
                <span class="vendor-step__dot"></span>
                Waiting
              </span>
              <span
                v-else-if="scope.row.vendorIqStep === STEP.DONE && isAlreadyExisted(scope.row)"
                class="vendor-step vendor-step--alert"
                :title="alertTooltip(scope.row)"
              >
                <span class="vendor-step__dot"></span>
                Exists
              </span>
              <span
                v-else-if="scope.row.vendorIqStep === STEP.DONE"
                class="vendor-step vendor-step--done"
              >
                <span class="vendor-step__dot"></span>
                Done
              </span>
              <span v-else class="vendor-step">
                {{ scope.row.vendorIqStep || '-' }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="venTaxId" label="vendTaxid" width="200" align="center">
            <template slot-scope="scope">
              <span class="vendor-tax-id">{{ scope.row.venTaxId || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="isRejected" label="ละเว้น" width="90" align="center" sortable>
            <template slot-scope="scope">
              <span v-if="scope.row.isRejected" class="vendor-flag vendor-flag--danger">
                <v-icon small>mdi-sign-caution</v-icon>
                ละเว้น
              </span>
              <span v-else class="vendor-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="isIncludeVat" label="รวมภาษี" width="90" align="center" sortable>
            <template slot-scope="scope">
              <span v-if="scope.row.isIncludeVat" class="vendor-flag vendor-flag--info">
                <v-icon small>mdi-approximately-equal-box</v-icon>
                รวม
              </span>
              <span v-else class="vendor-muted">-</span>
            </template>
          </el-table-column>
          <el-table-column prop="isForeign" label="ต่างประเทศ" width="120" align="center" sortable>
            <template slot-scope="scope">
              <span v-if="scope.row.isForeign" class="vendor-flag vendor-flag--success">
                <v-icon small>mdi-earth</v-icon>
                ต่างประเทศ
              </span>
              <span v-else class="vendor-muted">-</span>
            </template>
          </el-table-column>
          <!-- Show the 13-digit taxId as the vendor code in the UI; the
               Firestore venCode field uses REG-<taxId>-<branch> for
               namespacing/collision-safety, but that's an internal detail
               operators don't need to see. -->
          <el-table-column prop="venTaxId" label="รหัสผู้จำหน่าย" width="160" sortable>
            <template slot-scope="scope">
              <span class="vendor-code">{{ scope.row.venCode || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="venPrefix" label="คำนำหน้า" width="150"></el-table-column>
          <el-table-column prop="venName" label="ชื่อผู้จำหน่าย" width="250" sortable>
            <template slot-scope="scope">
              <div class="vendor-name-cell">
                {{ scope.row.venName || '-' }}
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="venBranch" label="รหัสสาขา" width="120">
            <template slot-scope="scope">
              <span class="vendor-branch">{{ scope.row.venBranch || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="รหัสค่าใช้จ่าย" width="160">
            <template slot-scope="scope">
              <div class="vendor-expense-codes">
                <span>P07 {{ (scope.row.defaultAcctP07 && scope.row.defaultAcctP07.expCode) || '-' }}</span>
                <span>P00 {{ (scope.row.defaultAcctP00 && scope.row.defaultAcctP00.expCode) || '-' }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="venAddr" label="ที่อยู่" min-width="320">
            <template slot-scope="scope">
              <div class="vendor-address">{{ scope.row.venAddr || '-' }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="remarks" label="Remarks" min-width="200">
            <template slot-scope="scope">
              <span class="vendor-remarks">{{ scope.row.remarks || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Action" width="280" align="center" fixed="right">
            <template slot-scope="scope">
              <div class="vendor-row-actions">
                <!-- Hide Edit on alert drafts: those are synthetic markers
                     pointing to a real vendor in PurchaseInvoiceVendors.
                     Editing the draft modifies a record nothing else
                     reads — operators should edit the real vendor in the
                     "ผู้จำหน่าย" tab instead. -->
                <el-tooltip
                  v-if="!isAlreadyExisted(scope.row)"
                  content="แก้ไข"
                  placement="top"
                >
                  <el-button
                    type="warning"
                    icon="el-icon-edit"
                    circle
                    size="mini"
                    @click="editVendor(scope.row)"
                  ></el-button>
                </el-tooltip>
                <el-tooltip content="ลบ" placement="top">
                  <el-button
                    type="danger"
                    icon="el-icon-delete"
                    circle
                    size="mini"
                    @click="confirmVendorDelete(scope.row)"
                  ></el-button>
                </el-tooltip>
                <!-- Step 1: Submit → write to PurchaseInvoiceVendors. -->
                <el-button
                  v-if="scope.row.vendorIqStep === STEP.DRAFT"
                  type="primary"
                  size="mini"
                  class="vendor-action-button"
                  :loading="rowBusy[scope.row.vendorId] === 'submit'"
                  @click="submitDraft(scope.row)"
                >Submit</el-button>
                <!-- Step 2: Save to Express. -->
                <el-button
                  v-else-if="scope.row.vendorIqStep === STEP.WAITING"
                  type="success"
                  size="mini"
                  class="vendor-action-button"
                  :loading="rowBusy[scope.row.vendorId] === 'express'"
                  @click="saveToExpress(scope.row)"
                >Save to Express</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
        </div>
        <v-row justify="start" align="center" class="vendor-pagination">
          <div class="vendor-page-range">
            {{ vendorItemsRangeText }}
          </div>
          <v-spacer />
          <v-pagination
            v-model="vendorPage"
            :length="vendorNumPages"
            :total-visible="6"
          ></v-pagination>
          <v-spacer />
          <div class="vendor-page-size">
            <select class="items-per-page-selector" v-model.number="vendorPageSize">
              <option v-for="opt in vendorPageSizeOptions" :key="opt" :value="opt">
                {{ opt }} / หน้า
              </option>
            </select>
          </div>
        </v-row>
      </div>
    </div>

    <!-- Vendor Form Dialog — writes to the drafts collection so editing a
         step-1 draft doesn't accidentally create a real vendor before the
         operator clicks Submit. -->
    <ap-vendor-form-dialog
      :is-open="showApVendorFormDialog"
      :is-edit-mode="isEditMode"
      :ap-vendor="apVendor"
      :companyID="companyId"
      :collection-name="'PurchaseInvoiceVendorDrafts'"
      :expenseList="expenseList"
      :paymentList="paymentList"
      :vatList="vatList"
      :whtList="whtList"
      @update:isOpen="showApVendorFormDialog = $event"
      @save="onVendorUpdated"
    />

    <!-- Single Vendor Delete Confirmation Dialog -->
    <confirm-delete-vendor-dialog
      :is-open="showVendorDeleteDialog"
      :doc-id-label="vendorToDelete && vendorToDelete.venName"
      @confirm="deleteVendor"
      @update:isOpen="showVendorDeleteDialog = $event"
    />

    <!-- Bulk Delete Confirmation Dialog -->
    <v-dialog v-model="showBulkDeleteDialog" persistent max-width="500px">
      <v-card>
        <v-card-title class="headline">ยืนยันการลบ</v-card-title>
        <v-card-text>
          คุณต้องการลบผู้จำหน่ายที่เลือกทั้งหมดหรือไม่?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="showBulkDeleteDialog = false">ยกเลิก</v-btn>
          <v-btn color="red darken-1" text @click="bulkDelete">ยืนยัน</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import fc from "@/firebaseConfig";
import firebase from "firebase/app";
import axios from "axios";
import ApVendorFormDialog from "@/components/admin/automation/ap-vendor-form-dialog.vue";
import confirmDeleteVendorDialog from "@/components/admin/automation/confirm-delete-vendor-dialog.vue";

const STEP = Object.freeze({ DRAFT: 1, WAITING: 2, DONE: 3 });

export default {
  name: "ApVendorIQ",
  components: { ApVendorFormDialog, confirmDeleteVendorDialog },
  props: {
    companyId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    STEP,
    snackbar: { show: false, message: "", color: "success" },
    vendorSearch: "",
    isInitializing: true,
    showApVendorFormDialog: false,
    isEditMode: false,
    apVendor: {},
    expenseList: [],
    vatList: [],
    whtList: [],
    paymentList: [],
    showVendorDeleteDialog: false,
    vendorToDelete: null,
    drafts: [],
    selectedVendorIds: [],
    selectedRows: [],
    showBulkDeleteDialog: false,
    showSkeletonAfterDelay: false,
    isSubmittingBulk: false,
    isSyncingExpressBulk: false,
    rowBusy: {},
    vendorPage: 1,
    vendorPageSize:
      localStorage.getItem("pageLimit") &&
      localStorage.getItem("pageLimit") === "auto"
        ? 10
        : Number.parseInt(localStorage.getItem("pageLimit")) || 10,
    vendorPageSizeOptions: [10, 25, 50, 100, 200],
    skeletonTimer: null,
    unsubscribeDrafts: null,
  }),
  computed: {
    filteredVendors() {
      const q = (this.vendorSearch || "").toLowerCase();
      const filtered = (this.drafts || []).filter((vendor) =>
        (vendor.venName || "").toLowerCase().includes(q) ||
        (vendor.venCode || "").toLowerCase().includes(q) ||
        (vendor.venTaxId || "").toLowerCase().includes(q)
      );
      // Sort by step (drafts first), then venCode.
      return filtered.slice().sort((a, b) => {
        const sa = a.vendorIqStep || 99;
        const sb = b.vendorIqStep || 99;
        if (sa !== sb) return sa - sb;
        return String(a.venCode || "").localeCompare(String(b.venCode || ""));
      });
    },
    paginatedVendors() {
      const start = (this.vendorPage - 1) * this.vendorPageSize;
      return this.filteredVendors.slice(start, start + this.vendorPageSize);
    },
    vendorNumPages() {
      return Math.max(1, Math.ceil(this.filteredVendors.length / this.vendorPageSize));
    },
    vendorItemsRangeText() {
      const total = this.filteredVendors.length;
      if (total === 0) return "0 - 0 จาก 0";
      const start = (this.vendorPage - 1) * this.vendorPageSize + 1;
      const end = Math.min(this.vendorPage * this.vendorPageSize, total);
      return `${start} - ${end} จาก ${total}`;
    },
    draftStepCount() {
      return this.filteredVendors.filter((vendor) => vendor.vendorIqStep === STEP.DRAFT).length;
    },
    waitingStepCount() {
      return this.filteredVendors.filter((vendor) => vendor.vendorIqStep === STEP.WAITING).length;
    },
    doneStepCount() {
      return this.filteredVendors.filter(
        (vendor) => vendor.vendorIqStep === STEP.DONE && !this.isAlreadyExisted(vendor)
      ).length;
    },
    alreadyExistedStepCount() {
      return this.filteredVendors.filter(
        (vendor) => vendor.vendorIqStep === STEP.DONE && this.isAlreadyExisted(vendor)
      ).length;
    },
    selectedStep1Count() {
      return this.selectedRows.filter((r) => r.vendorIqStep === STEP.DRAFT).length;
    },
    selectedStep2Count() {
      return this.selectedRows.filter((r) => r.vendorIqStep === STEP.WAITING).length;
    },
    isInitialLoading() {
      return this.isInitializing;
    },
    isRefreshing() {
      return false;
    },
    showInitialSkeleton() {
      return this.isInitialLoading && this.showSkeletonAfterDelay;
    },
    skeletonRowCount() {
      return Math.min(this.vendorPageSize || 10, 10);
    },
  },
  watch: {
    vendorSearch() {
      this.vendorPage = 1;
    },
    vendorNumPages(newVal) {
      if (this.vendorPage > newVal) this.vendorPage = newVal;
    },
    vendorPageSize(newVal) {
      localStorage.setItem("pageLimit", String(newVal));
      this.vendorPage = 1;
    },
    isInitialLoading: {
      immediate: true,
      handler(loading) {
        if (loading) {
          this.skeletonTimer = setTimeout(() => {
            this.showSkeletonAfterDelay = true;
          }, 200);
        } else {
          if (this.skeletonTimer) {
            clearTimeout(this.skeletonTimer);
            this.skeletonTimer = null;
          }
          this.showSkeletonAfterDelay = false;
        }
      },
    },
  },
  beforeDestroy() {
    if (this.skeletonTimer) clearTimeout(this.skeletonTimer);
    if (this.unsubscribeDrafts) this.unsubscribeDrafts();
  },
  mounted() {
    this.init();
    this.getMasterDataList();
  },
  methods: {
    handleSelectionChange(selection) {
      this.selectedRows = selection;
      this.selectedVendorIds = selection.map((item) => item.vendorId);
    },
    isAlreadyExisted(row) {
      return Boolean(row && (row.vendorAlreadyExisted || row.expressAlreadyExisted));
    },
    alertTooltip(row) {
      if (!row) return "";
      if (row.expressAlreadyExisted) {
        return "ผู้จำหน่ายมีอยู่ใน Express (APMAS) แล้ว — ข้ามการเพิ่ม";
      }
      if (row.vendorAlreadyExisted) {
        return "เอกสาร reject แต่ผู้จำหน่ายมีอยู่แล้วในระบบ";
      }
      return "";
    },
    preprocess(text) {
      return text
        .toLowerCase()
        .replace(/[^\w\s฀-๿]/g, "")
        .replace(/\s+/g, " ")
        .trim();
    },
    draftRef(vendorId) {
      return fc.companies
        .doc(this.companyId)
        .collection("PurchaseInvoiceVendorDrafts")
        .doc(vendorId);
    },
    vendorRef(vendorId) {
      return fc.companies
        .doc(this.companyId)
        .collection("PurchaseInvoiceVendors")
        .doc(vendorId);
    },
    setRowBusy(vendorId, kind) {
      this.$set(this.rowBusy, vendorId, kind);
    },
    clearRowBusy(vendorId) {
      this.$delete(this.rowBusy, vendorId);
    },
    confirmVendorDelete(vendor) {
      this.vendorToDelete = vendor;
      this.showVendorDeleteDialog = true;
    },
    async deleteVendor() {
      if (!this.vendorToDelete) return;
      const vendorId = this.vendorToDelete.vendorId;
      try {
        await this.draftRef(vendorId).delete();
        this.notify("success", "ลบ draft เรียบร้อย");
      } catch (e) {
        console.error("Error deleting draft:", e);
        this.notify("error", `ลบ draft ล้มเหลว: ${e.message}`);
      }
      this.vendorToDelete = null;
      this.showVendorDeleteDialog = false;
    },
    editVendor(vendor) {
      this.isEditMode = true;
      this.apVendor = JSON.parse(JSON.stringify(vendor));
      this.showApVendorFormDialog = true;
    },
    onVendorUpdated() {
      // The form dialog has already written to PurchaseInvoiceVendorDrafts
      // (see :collection-name prop) and the snapshot listener will pick the
      // change up. Nothing else to do here besides surfacing a success toast.
      this.notify("success", "แก้ไขข้อมูลเรียบร้อย");
    },
    async getMasterDataList() {
      const masterRef = await fc.companies.doc(this.companyId).collection("PurchaseInvoiceMaster").get();
      const masterList = masterRef.docs.map((doc) => ({ ...doc.data(), docId: doc.id }));
      this.expenseList = (masterList.find((item) => item.docId === "Expense") || {}).data || [];
      this.vatList = (masterList.find((item) => item.docId === "Vat") || {}).data || [];
      this.whtList = (masterList.find((item) => item.docId === "WithholdingTax") || {}).data || [];
      this.paymentList = (masterList.find((item) => item.docId === "PaymentAccount") || {}).data || [];
    },
    notify(type, message) {
      this.snackbar = { show: true, message, color: type };
    },
    init() {
      this.isInitializing = true;
      if (!this.companyId) {
        this.isInitializing = false;
        return;
      }
      // Live-listen to the drafts collection. autoCreatedFromRegistry filter
      // keeps the view focused on registry-seeded rows even if anything else
      // ever writes to this collection.
      this.unsubscribeDrafts = fc.companies
        .doc(this.companyId)
        .collection("PurchaseInvoiceVendorDrafts")
        .onSnapshot(
          (snap) => {
            this.drafts = snap.docs
              .map((d) => ({ vendorId: d.id, ...d.data() }))
              .filter((v) => v.autoCreatedFromRegistry === true);
            this.isInitializing = false;
          },
          (err) => {
            console.error("Error listening to drafts:", err);
            this.notify("error", "เกิดข้อผิดพลาดในการดึงข้อมูล draft");
            this.isInitializing = false;
          }
        );
    },
    /** Step 1: copy draft to PurchaseInvoiceVendors and mark draft as
     *  vendorIqStep:2 (waiting for Express). Idempotent on the destination
     *  via merge:true. */
    async submitDraft(draft) {
      const vendorId = draft.vendorId;
      this.setRowBusy(vendorId, "submit");
      try {
        // Idempotency guard: only run on step-1 drafts. Without this a stale
        // row in the table could be re-submitted, re-overwriting a vendor
        // that the operator already submitted via another tab.
        if (draft.vendorIqStep !== STEP.DRAFT) {
          throw new Error("Draft is not in step 1");
        }
        if (!draft.venCode || !String(draft.venCode).trim()) {
          throw new Error("ไม่มีรหัสบริษัท (venCode)");
        }
        const now = firebase.firestore.FieldValue.serverTimestamp();
        // Strip draft-only state fields before writing to vendors.
        const { vendorIqStep, submittedAt, expressSyncedAt, ...vendorPayload } = draft;
        vendorPayload.venSearchString = this.preprocess(
          `${vendorPayload.venName || ""} ${vendorPayload.venBranch === "00000" ? vendorPayload.venBranch : ""} ${vendorPayload.venTaxId || ""}`
        );
        vendorPayload.vendorId = vendorId;
        if (!vendorPayload.createdAt) vendorPayload.createdAt = now;
        vendorPayload.updatedAt = now;

        const batch = fc.db.batch();
        batch.set(this.vendorRef(vendorId), vendorPayload, { merge: true });
        batch.update(this.draftRef(vendorId), {
          vendorIqStep: STEP.WAITING,
          submittedAt: now,
          updatedAt: now,
        });
        await batch.commit();
        this.notify("success", `Submit สำเร็จ — ${vendorPayload.venName || vendorId}`);
      } catch (e) {
        console.error("submitDraft failed:", e);
        this.notify("error", `Submit ล้มเหลว: ${e.message}`);
      } finally {
        this.clearRowBusy(vendorId);
      }
    },
    /** Build the Express vendor payload from a Firebase vendor draft.
     *  See field-mapping table in PR/issue. Pure function — easy to test. */
    buildExpressPayload(draft) {
      const addr = String(draft.venAddr || "");
      // 50-char chunks; ADDR03 absorbs everything past pos 100.
      const ADDR01 = addr.slice(0, 50);
      const ADDR02 = addr.slice(50, 100);
      const ADDR03 = addr.slice(100, 150);

      // Express CREDAT — YYYYMMDD. Adjust if Express expects ISO/Thai-Buddhist.
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const CREDAT = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;

      // Numeric APMAS columns (PAYTRM, VATRAT, FLGVAT, BALANCE, CHQPAY,
      // CRLINE, TAXRAT, DISC) are sent as JS numbers — the gateway
      // type-checks against the FoxPro column type and rejects strings of
      // digits with "expected a number".
      return {
        // SUPCOD = real 13-digit taxId. The draft's docId / venCode is the
        // synthetic "REG-<taxId>-<branch>" used internally for collision
        // safety, but Express needs the actual tax ID.
        SUPCOD: draft.venCode || "",
        SUPTYP: "00",
        ONHOLD: "",
        // SUPNAM was not in the spec but Express almost certainly requires
        // a primary vendor name. Remove this line if Express rejects it.
        SUPNAM: draft.venName || "",
        PRENAM: draft.venPrefix || "",
        ADDR01: ADDR01,
        ADDR02: ADDR02,
        ADDR03: ADDR03,
        ZIPCOD: draft.venZipCode || "",
        TELNUM: "",
        CONTACT: "",
        SUPNAM2: "",
        PAYTRM: 0,
        PAYCOND: "",
        DLVBY: "",
        VATRAT: 7,
        FLGVAT: 0,
        DISC: 0,
        BALANCE: 0,
        CHQPAY: 0,
        CRLINE: 0,
        LASRCV: "",
        ACCNUM: "2120-01",
        REMARK: "",
        TAXID: draft.venTaxId || "",
        ORGNUM: 0,
        TAXDES: "",
        TAXRAT: 0,
        TAXTYP: "",
        TAXCOND: "",
        CREBY: "BIT9",
        CREDAT: CREDAT,
        USERID: "BIT9",
        CHGDAT: "",
        STATUS: "",
        INACTDAT: "",
      };
    },
    /** Step 2: push the draft to Express ERP via the export endpoint. On
     *  2xx, stamp expressSyncedAt on the vendor and delete the draft so
     *  the row leaves the VendorIQ tab. On failure, persist the error on
     *  the draft so operators can see it inline. */
    async saveToExpress(draft) {
      const vendorId = draft.vendorId;
      this.setRowBusy(vendorId, "express");
      try {
        if (draft.vendorIqStep !== STEP.WAITING) {
          throw new Error("Draft is not in step 2 (waiting for Express)");
        }

        const payload = this.buildExpressPayload(draft);

        // === FoxPro APMAS insert ===
        // Mirrors apiFoxproSyncVendors (the read side) — same gateway, same
        // config doc, just the WRITE counterpart.
        const url = `${fc.cloudFunctionUrl}apiFoxproInsertVendor`;
        const response = await axios.post(
          url,
          { companyId: this.companyId, vendor: payload },
          { headers: { "Content-Type": "application/json" } }
        );

        if (!response || !response.data || response.data.success === false) {
          const errObj = response && response.data && response.data.error;
          const msg =
            (errObj && (errObj.message || errObj.code)) ||
            (response && response.data && response.data.message) ||
            "FoxPro gateway returned non-success";
          throw new Error(msg);
        }
        // === End FoxPro APMAS insert ===

        const wasSkipped =
          response.data &&
          response.data.data &&
          response.data.data.skipped === true;
        const now = firebase.firestore.FieldValue.serverTimestamp();
        const batch = fc.db.batch();
        batch.update(this.vendorRef(vendorId), {
          expressSyncedAt: now,
          updatedAt: now,
        });
        if (wasSkipped) {
          // Anomaly path: SUPCOD was already in APMAS. Don't delete the draft
          // — instead transition to terminal step 3 ("done") and stamp a
          // human-readable remark so the operator sees what happened in the
          // VendorIQ table. They bulk-delete completed rows when ready.
          const today = new Date().toISOString().slice(0, 10);
          batch.update(this.draftRef(vendorId), {
            vendorIqStep: STEP.DONE,
            remarks: `อยู่ใน Express อยู่แล้ว — ตรวจสอบเมื่อ ${today}`,
            expressAlreadyExisted: true,
            completedAt: now,
            updatedAt: now,
          });
        } else {
          // Normal path: vendor was successfully appended to APMAS, no
          // operator review needed — clean up the draft.
          batch.delete(this.draftRef(vendorId));
        }
        await batch.commit();

        const successMsg = wasSkipped
          ? `อยู่ใน Express อยู่แล้ว — ${draft.venName || vendorId}`
          : `ส่งข้อมูลไปยัง Express สำเร็จ — ${draft.venName || vendorId}`;
        this.notify("success", successMsg);
      } catch (e) {
        console.error("saveToExpress failed:", e);
        const msg =
          (e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
          e.message ||
          String(e);
        try {
          await this.draftRef(vendorId).update({
            lastExpressError: msg,
            lastExpressAttemptAt: firebase.firestore.FieldValue.serverTimestamp(),
          });
        } catch (writeErr) {
          console.warn("Failed to persist lastExpressError:", writeErr);
        }
        this.notify("error", `Save to Express ล้มเหลว: ${msg}`);
      } finally {
        this.clearRowBusy(vendorId);
      }
    },
    async bulkSubmit() {
      const targets = this.selectedRows.filter((r) => r.vendorIqStep === STEP.DRAFT);
      if (targets.length === 0) return;
      this.isSubmittingBulk = true;
      try {
        for (const row of targets) {
          // eslint-disable-next-line no-await-in-loop
          await this.submitDraft(row);
        }
      } finally {
        this.isSubmittingBulk = false;
      }
    },
    async bulkSaveToExpress() {
      const targets = this.selectedRows.filter((r) => r.vendorIqStep === STEP.WAITING);
      if (targets.length === 0) return;
      this.isSyncingExpressBulk = true;
      try {
        for (const row of targets) {
          // eslint-disable-next-line no-await-in-loop
          await this.saveToExpress(row);
        }
      } finally {
        this.isSyncingExpressBulk = false;
      }
    },
    confirmBulkDelete() {
      this.showBulkDeleteDialog = true;
    },
    async bulkDelete() {
      const ids = [...this.selectedVendorIds];
      this.showBulkDeleteDialog = false;
      try {
        const batch = fc.db.batch();
        ids.forEach((id) => batch.delete(this.draftRef(id)));
        await batch.commit();
        this.selectedVendorIds = [];
        this.selectedRows = [];
        this.notify("success", `ลบ draft ${ids.length} รายการสำเร็จ`);
      } catch (e) {
        console.error("bulkDelete failed:", e);
        this.notify("error", `ลบ draft ล้มเหลว: ${e.message}`);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.vendor-iq-page {
  margin: 2rem 1.25rem 0;
}

.vendor-iq-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 1rem;
}

.vendor-iq-title {
  margin: 0;
  color: #111827;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: 0;
}

.vendor-iq-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
  color: #4b5563;
  font-size: 0.75rem;
}

.vendor-iq-summary span {
  padding: 0.25rem 0.625rem;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f8fafc;
  line-height: 1.35;
}

.vendor-iq-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.625rem;
}

.vendor-table-card {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
}

.vendor-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
}

.vendor-search {
  flex: 1 1 20rem;
  max-width: 28rem;
}

.vendor-search ::v-deep .v-input__slot {
  border-radius: 8px;
  background: #f9fafb;
}

.vendor-toolbar__hint {
  flex: 0 0 auto;
  color: #64748b;
  font-size: 0.75rem;
  white-space: nowrap;
}

.vendor-table-wrap {
  position: relative;
  overflow-x: auto;
}

.vendor-progress {
  margin: 0;
}

.vendor-iq-table {
  color: #263238;
  font-size: 0.8125rem;
}

.vendor-iq-table ::v-deep .el-table__header-wrapper th,
.vendor-iq-table ::v-deep .el-table__fixed-header-wrapper th {
  background: #f7fafc;
  color: #334155;
  font-weight: 700;
}

.vendor-iq-table ::v-deep th.el-table__cell,
.vendor-iq-table ::v-deep td.el-table__cell {
  border-color: #edf2f7;
}

.vendor-iq-table ::v-deep .el-table__body td {
  padding: 0.625rem 0;
}

.vendor-iq-table ::v-deep .el-table__row:hover > td {
  background: #f8fbff !important;
}

.vendor-iq-table ::v-deep .el-table__fixed,
.vendor-iq-table ::v-deep .el-table__fixed-right {
  box-shadow: 0 0 14px rgba(15, 23, 42, 0.08);
}

.vendor-iq-table ::v-deep .el-table__empty-block {
  min-height: 15rem;
}

.vendor-iq-table ::v-deep .el-table__empty-text {
  color: #64748b;
}

.vendor-index,
.vendor-code,
.vendor-branch,
.vendor-tax-id {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 1.625rem;
  border-radius: 6px;
  font-weight: 600;
  line-height: 1;
}

.vendor-index {
  min-width: 1.875rem;
  background: #eef2ff;
  color: #4338ca;
}

.vendor-code {
  padding: 0.25rem 0.5rem;
  background: #f1f5f9;
  color: #0f172a;
}

.vendor-branch,
.vendor-tax-id {
  padding: 0.25rem 0.5rem;
  background: #f8fafc;
  color: #475569;
}

.vendor-step,
.vendor-flag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-height: 1.625rem;
  padding: 0.25rem 0.625rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
}

.vendor-step {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
}

.vendor-step__dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  background: currentColor;
}

.vendor-step--draft {
  border-color: #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
}

.vendor-step--waiting {
  border-color: #fde68a;
  background: #fffbeb;
  color: #b45309;
}

.vendor-step--done {
  border-color: #d1d5db;
  background: #f3f4f6;
  color: #6b7280;
}

.vendor-step--alert {
  border-color: #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.vendor-flag--danger {
  background: #fef2f2;
  color: #b91c1c;
}

.vendor-flag--info {
  background: #ecfeff;
  color: #0e7490;
}

.vendor-flag--success {
  background: #ecfdf5;
  color: #047857;
}

.vendor-flag .v-icon {
  color: inherit;
}

.vendor-muted,
.vendor-remarks {
  color: #94a3b8;
}

.vendor-name-cell {
  max-width: 100%;
  overflow: hidden;
  color: #111827;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vendor-expense-codes {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.vendor-expense-codes span {
  width: fit-content;
  padding: 0.1875rem 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #ffffff;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 600;
}

.vendor-address {
  display: -webkit-box;
  overflow: hidden;
  color: #475569;
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.vendor-row-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  white-space: nowrap;
}

.vendor-row-actions ::v-deep .el-button.is-circle {
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
}

.vendor-row-actions ::v-deep .el-button + .el-button {
  margin-left: 0;
}

.vendor-action-button {
  border-radius: 6px;
  font-weight: 700;
}

.vendor-pagination {
  margin: 0;
  padding: 0.875rem 1rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

.vendor-page-range {
  padding: 0 0.5rem;
  color: #475569;
  font-size: 0.8125rem;
  font-weight: 600;
}

.vendor-page-size {
  padding: 0 0.5rem;
}

.vendor-pagination ::v-deep .v-pagination__item,
.vendor-pagination ::v-deep .v-pagination__navigation {
  box-shadow: none;
  border: 1px solid #e2e8f0;
}

.items-per-page-selector {
  min-height: 2rem;
  padding: 0.25rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background-color: #ffffff;
  color: #334155;
  text-align: center;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: inherit;
}

.vendor-skeleton {
  width: 100%;
  overflow-x: auto;
}

.vendor-skeleton__inner {
  min-width: 2310px;
}

.vendor-skeleton__header,
.vendor-skeleton__row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #edf2f7;
}

.vendor-skeleton__header {
  height: 44px;
  background: #f7fafc;
  font-weight: 600;
  color: #64748b;
  font-size: 13px;
}

.vendor-skeleton__row {
  height: 54px;
}

.vendor-skeleton__cell {
  flex: 0 0 auto;
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.vendor-skeleton__cell--flex {
  flex: 1 1 auto;
  min-width: 300px;
}

.skel-bar {
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    #e2e8f0 0%,
    #f8fafc 50%,
    #e2e8f0 100%
  );
  background-size: 200% 100%;
  animation: vendor-skeleton-shimmer 1.4s ease-in-out infinite;
}

.skel-bar--checkbox {
  width: 16px;
  height: 16px;
  border-radius: 2px;
}

.skel-bar--circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
}

@keyframes vendor-skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (max-width: 768px) {
  .vendor-iq-header,
  .vendor-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .vendor-iq-actions {
    justify-content: flex-start;
  }

  .vendor-search {
    max-width: none;
  }

  .vendor-toolbar__hint {
    white-space: normal;
  }
}
</style>
